import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  compactNumberTitle,
  formatDisplayNumber,
  formatExactNumber,
} from "../utils/formatNumber";

interface FormattedNumberProps {
  value: number;
  /** compact: abbreviated; exact: always full number */
  mode?: "compact" | "exact";
  /** Tap/click to reveal exact value when compact form is used */
  interactive?: boolean;
  className?: string;
  prefix?: string;
  suffix?: string;
}

export function FormattedNumber({
  value,
  mode = "compact",
  interactive = false,
  className,
  prefix = "",
  suffix = "",
}: FormattedNumberProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const exact = formatExactNumber(value);
  const display =
    mode === "exact" ? exact : formatDisplayNumber(value, "compact");
  const hasExactHint = mode === "compact" && compactNumberTitle(value) != null;

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [open]);

  const content = (
    <>
      {prefix}
      {display}
      {suffix}
    </>
  );

  if (!interactive || !hasExactHint) {
    return (
      <span className={className} title={hasExactHint ? exact : undefined}>
        {content}
      </span>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        setOpen((prev) => !prev);
      }}
      className={clsx(
        "relative inline-flex items-center cursor-pointer select-none",
        "hover:opacity-90 active:scale-[0.98] transition-transform",
        className,
      )}
      aria-label={exact}
      aria-expanded={open}
    >
      {content}
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+4px)] z-50 whitespace-nowrap rounded-lg bg-kingdom-dark border border-kingdom-glow px-2 py-1 text-[11px] font-semibold text-white shadow-lg tabular-nums pointer-events-none"
        >
          {exact}
        </span>
      )}
    </button>
  );
}
